import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toast";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { logger } from "@/services/logger";
import { AuthProvider } from "@/hooks/useAuth";
import { CorporateAccessProvider, useCorporateAccess } from "@/hooks/useCorporateAccess";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FlexLayout, Spinner } from "@/design-system/salt";
import { useAuth } from "@/hooks/useAuth";
import { useBuyerHubAccess } from "@/hooks/useBuyerHubAccess";
import { buyerHubHomePath } from "@/services/buyerHub/hubSend";
import DashboardLayoutRoute from "@/components/dashboard/DashboardLayoutRoute";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import IssuerHubGuard from "@/components/issuer-hub/IssuerHubGuard";
import BuyerHubGuard from "@/components/buyer-hub-app/BuyerHubGuard";

// Sentry-aware Routes wrapper — maps parameterized URLs to route patterns.
// Falls back to plain Routes when Sentry is not initialized (dev / no DSN).
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Suspense fallback
const PageLoader = () => (
  <FlexLayout align="center" justify="center" style={{minHeight: "100vh", background: "var(--salt-container-primary-background)"}}>
    <Spinner aria-label="Loading application" size="large" />
  </FlexLayout>
);

// Main pages — lazy loaded
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ReportsPage = lazy(() => import("./pages/Reports"));
const CorporatesPage = lazy(() => import("./pages/Corporates"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const CampaignAddSuppliers = lazy(() => import("./pages/CampaignAddSuppliers"));
const EnrollmentsPage = lazy(() => import("./pages/Enrollments"));
const ActionCenterPage = lazy(() => import("./pages/ActionCenter"));
const StrategyPage = lazy(() => import("./pages/Strategy"));
const CampaignStrategyPlanning = lazy(() => import("./pages/CampaignStrategyPlanning"));
const IntelligencePage = lazy(() => import("./pages/Intelligence"));
const SupplierOnboarding = lazy(() => import("./pages/SupplierOnboarding"));
const Reminders = lazy(() => import("./pages/Reminders"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const DesignSystem = lazy(() => import("./pages/admin/DesignSystem"));
const CanSpamReadiness = lazy(() => import("./pages/admin/CanSpamReadiness"));
const AdminNegotiationTemplateEdit = lazy(
  () => import("./pages/admin/AdminNegotiationTemplateEdit"),
);
const AdminNegotiationTemplateCreate = lazy(
  () => import("./pages/admin/AdminNegotiationTemplateCreate"),
);
const AccountSecurity = lazy(() => import("./pages/AccountSecurity"));
const ComplianceCenter = lazy(() => import("./pages/ComplianceCenter"));

// Corporate sub-pages
const CorporateContacts = lazy(() => import("./pages/corporate/CorporateContacts"));
const CorporateContactDetail = lazy(() => import("./pages/corporate/CorporateContactDetail"));
const CorporateTradeProfiles = lazy(() => import("./pages/corporate/CorporateTradeProfiles"));
const CorporateTradeProfileDetail = lazy(
  () => import("./pages/corporate/CorporateTradeProfileDetail"),
);
const CorporateCampaigns = lazy(() => import("./pages/corporate/CorporateCampaigns"));
const CorporateAPSpendFiles = lazy(() => import("./pages/corporate/CorporateAPSpendFiles"));
const CorporateCampaignCreate = lazy(() => import("./pages/corporate/CorporateCampaignCreate"));
const CorporateEmailStudio = lazy(() => import("./pages/corporate/CorporateEmailStudio"));
const CorporatePolicies = lazy(() => import("./pages/corporate/CorporatePolicies"));
const CorporateSettings = lazy(() => import("./pages/corporate/CorporateSettings"));
const CorporateStrategyConfigCreate = lazy(
  () => import("./pages/corporate/CorporateStrategyConfigCreate"),
);
const CorporateGoals = lazy(() => import("./pages/corporate/CorporateGoals"));
const CorporateGoalCreate = lazy(() => import("./pages/corporate/CorporateGoalCreate"));
const CorporateContactCreate = lazy(() => import("./pages/corporate/CorporateContactCreate"));
const CorporateVendorCreate = lazy(() => import("./pages/corporate/CorporateVendorCreate"));
const LeadEntryForm = lazy(() => import("./components/lead-entry/lead-entry-form.component"));
const CorporateNegotiationTemplateCreate = lazy(
  () => import("./pages/corporate/CorporateNegotiationTemplateCreate"),
);
const CorporatePlaybook = lazy(() => import("./pages/corporate/CorporatePlaybook"));
const CorporateProposal = lazy(() => import("./pages/corporate/CorporateProposal"));

const ProposalPlanning = lazy(
  () => import("./features/proposal-os/pages/OpportunityMapPage"),
);
const ProposalOverview = lazy(
  () => import("./features/proposal-os/pages/StrategyPlannerPage"),
);

const CorporateTemplates = lazy(() => import("./pages/corporate/CorporateTemplates"));
const CorporatesCreate = lazy(() => import("./pages/CorporatesCreate"));
const CorporateAPSpendFileUpload = lazy(
  () => import("./pages/corporate/CorporateAPSpendFileUpload"),
);
const UploadFinancialReports = lazy(() => import("./pages/corporate/UploadFinancialReports"));
const SupplierCorporateOnboardingHub = lazy(
  () => import("./pages/corporate/SupplierCorporateOnboardingHub"),
);
const SupplierCorporateOnboardingPackageWizard = lazy(
  () => import("./pages/corporate/SupplierCorporateOnboardingPackageWizard"),
);

// AP Spend
const APSpendFileDetail = lazy(() => import("./pages/APSpendFileDetail"));
const APSpendFilesUpload = lazy(() => import("./pages/APSpendFilesUpload"));

// Public pages
const CompleteTradeProfile = lazy(() => import("./pages/CompleteTradeProfile"));
const CorporateTask = lazy(() => import("./pages/CorporateTask"));
const EnrollPage = lazy(() => import("./pages/EnrollPage"));
const ProspectingLandingPage = lazy(() => import("./pages/ProspectingLandingPage"));
const SupplierPortal = lazy(() => import("./pages/SupplierPortal"));
const DnsSetup = lazy(() => import("./pages/DnsSetup"));
const PortalJourney = lazy(() => import("./pages/portal/PortalJourney"));
const PortalJourneyItem = lazy(() => import("./pages/portal/PortalJourneyItem"));
const SpendRegistration = lazy(() => import("./pages/SpendRegistration"));
const PlatformAgreement = lazy(() => import("./pages/PlatformAgreement"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const CampaignExecutiveReport = lazy(() => import("./pages/CampaignExecutiveReport"));

// Onboarding flows
const CardProgramOnboardingPage = lazy(() => import("./pages/CardProgramOnboarding"));
const BuyerOnboardingPage = lazy(() => import("./pages/BuyerOnboardingPage"));
const PersonaOnboardingRouter = lazy(
  () => import("./components/onboarding/PersonaOnboardingRouter"),
);

// Misc
const TreasuryConversationGuidePage = lazy(() => import("./pages/TreasuryConversationGuide"));
const TreasuryConversationGuideCreatePage = lazy(
  () => import("./pages/TreasuryConversationGuideCreate"),
);
const DataHygienePage = lazy(() => import("./pages/DataHygiene"));
const TreasuryConversationGuideDetailPage = lazy(
  () => import("./pages/TreasuryConversationGuideDetail"),
);
const NotFound = lazy(() => import("./pages/NotFound"));

// Issuer Hub
const IssuerHubLayoutRoute = lazy(() => import("./pages/issuer-hub/IssuerHubLayoutRoute"));
const IssuerPipelinePage = lazy(() => import("./pages/IssuerPipeline"));
const IssuerCampaignOverviewPage = lazy(() => import("./pages/IssuerCampaignOverview"));
const IssuerFinancialModelingPage = lazy(() => import("./pages/IssuerFinancialModeling"));
const IssuerQuotaPage = lazy(() => import("./pages/IssuerQuota"));
const IssuerProspectingPagesPage = lazy(() => import("./pages/IssuerProspectingPages"));

// Buyer Hub (U1)
const BuyerHubLayoutRoute = lazy(() => import("./pages/buyer-hub-app/BuyerHubLayoutRoute"));
const BuyerHubHome = lazy(() => import("./pages/buyer-hub-app/BuyerHubHome"));
const BuyerHubSpendPage = lazy(() => import("./pages/buyer-hub-app/BuyerHubSpendPage"));
const BuyerHubProposalPage = lazy(() => import("./pages/buyer-hub-app/BuyerHubProposalPage"));
const BuyerHubPlanningPage = lazy(() => import("./pages/buyer-hub-app/BuyerHubPlanningPage"));
const BuyerHubReportingPage = lazy(() => import("./pages/buyer-hub-app/BuyerHubReportingPage"));

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Safety net — captures errors from query functions that don't call logger.error
      // before throwing. Service layers that already log produce a duplicate Sentry event
      // which Sentry deduplicates by fingerprint.
      logger.error(
        `React Query error [${String(query.queryKey)}]`,
        error instanceof Error ? error : new Error(String(error)),
        { queryKey: String(query.queryKey) },
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      // Safety net — same rationale as QueryCache above.
      logger.error(
        "React Query mutation error",
        error instanceof Error ? error : new Error(String(error)),
      );
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/** Portal-aware root redirect */
function RootRedirect() {
  const { loading: authLoading, user } = useAuth();
  const {
    isPortalUser,
    primaryCorporateId,
    loading: accessLoading,
    corporateIds,
  } = useCorporateAccess();
  const { isHubBuyerUser, hubCorporateId, loading: hubLoading } = useBuyerHubAccess();

  if (authLoading || accessLoading || hubLoading) {
    return (
      <FlexLayout align="center" justify="center" style={{minHeight: "100vh", background: "var(--salt-container-primary-background)"}}>
        <Spinner aria-label="Loading account" size="large" />
      </FlexLayout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (isHubBuyerUser && hubCorporateId !== null) {
    return <Navigate to={buyerHubHomePath(hubCorporateId)} replace />;
  }

  if (isPortalUser && primaryCorporateId) {
    return <Navigate to={`/corporate/${primaryCorporateId}/goals`} replace />;
  }

  if (isPortalUser && corporateIds.length === 0) {
    return <Navigate to="/auth" replace />;
  }

  return <Navigate to="/corporates" replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CorporateAccessProvider>
        <Toaster />
        <SpeedInsights />
        <BrowserRouter>
          <ErrorBoundary showDialog>
            <Suspense fallback={<PageLoader />}>
              <SentryRoutes>
                {/* Auth route - public */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/post-auth" element={<PersonaOnboardingRouter />} />

                {/* Root redirect - portal-aware */}
                <Route path="/" element={<RootRedirect />} />

                {/* Prospect Landing Page — public, no auth required */}
                <Route path="/prospect/:token" element={<ProspectingLandingPage />} />

                {/* Trade Profile Completion - public for external users */}
                <Route path="/complete-profile/:token" element={<CompleteTradeProfile />} />

                {/* Supplier Enrollment Landing Page - public */}
                <Route path="/enroll/:token" element={<EnrollPage />} />

                {/* Corporate Task Links - public for external corporate users */}
                <Route path="/task/:token" element={<CorporateTask />} />

                {/* Spend Registration - public external form */}
                <Route path="/register-spend" element={<SpendRegistration />} />
                <Route path="/register-spend/:token" element={<SpendRegistration />} />

                {/* Unsubscribe - public CAN-SPAM compliance */}
                <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

                {/* T3.3: White-Label Supplier Self-Service Portal — public, token-gated */}
                <Route path="/supplier-portal/:token" element={<SupplierPortal />} />

                {/* Track 5: buyer sysadmin DNS setup — public, token-gated, no account required.
                The buyer's DNS admin must be able to finish setup without becoming an
                application user (roadmap acceptance criterion 2). */}
                <Route path="/dns-setup/:token" element={<DnsSetup />} />

                {/* The Onboarding Portal (Aaron's name ruling, slice 4 — formerly the standalone
                apps/onboarding-portal Next app; the in-app SupplierOnboarding/SupplierPortal
                pages are a different surface and keep their names). Ported in slice 2 of
                tasks/portal-port-plan.md) — public routes; access control is the
                __Host-portal_session cookie checked by /api/portal/** on every read,
                exactly like the other token-gated external surfaces above. */}
                <Route path="/portal/j/:journeyId" element={<PortalJourney />} />
                <Route path="/portal/j/:journeyId/items/:itemId" element={<PortalJourneyItem />} />

                {/* T3.2: Campaign Executive Report — public, token-gated */}
                <Route path="/campaign-report/:token" element={<CampaignExecutiveReport />} />

                {/* Legal pages - public */}
                <Route path="/platform-agreement" element={<PlatformAgreement />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                {/* Onboarding flows — full-screen, no sidebar */}
                <Route
                  path="/onboarding/card-program"
                  element={
                    <ProtectedRoute>
                      <CardProgramOnboardingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding/standard"
                  element={
                    <ProtectedRoute>
                      <BuyerOnboardingPage />
                    </ProtectedRoute>
                  }
                />

                {/* All dashboard routes — persistent layout */}
                <Route element={<DashboardLayoutRoute />}>
                  {/* Main dashboard routes */}
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute platformOnly>
                        <ReportsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/proof" element={<Navigate to="/reports" replace />} />
                  <Route path="/proof/:corporateId" element={<Navigate to="/reports" replace />} />
                  <Route
                    path="/corporates"
                    element={
                      <ProtectedRoute platformOnly>
                        <CorporatesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporates/create"
                    element={
                      <ProtectedRoute platformOnly>
                        <CorporatesCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ap-spend-files/:fileId"
                    element={
                      <ProtectedRoute platformOnly>
                        <APSpendFileDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ap-spend-files/upload"
                    element={
                      <ProtectedRoute platformOnly>
                        <APSpendFilesUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaigns/:campaignId"
                    element={
                      <ProtectedRoute>
                        <CampaignDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaigns/:campaignId/add-suppliers"
                    element={
                      <ProtectedRoute>
                        <CampaignAddSuppliers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/enrollments"
                    element={
                      <ProtectedRoute platformOnly>
                        <EnrollmentsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/tasks" element={<Navigate to="/action-center" replace />} />
                  <Route
                    path="/action-center"
                    element={
                      <ProtectedRoute platformOnly>
                        <ActionCenterPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/action-center/automations"
                    element={<Navigate to="/action-center" replace />}
                  />
                  <Route path="/rep-room" element={<Navigate to="/action-center" replace />} />
                  <Route
                    path="/negotiation"
                    element={<Navigate to="/admin/settings?tab=negotiation" replace />}
                  />
                  <Route
                    path="/strategy"
                    element={
                      <ProtectedRoute platformOnly>
                        <StrategyPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/intelligence"
                    element={
                      <ProtectedRoute platformOnly requiredRoles={["admin", "manager"]}>
                        <IntelligencePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/supplier-onboarding"
                    element={
                      <ProtectedRoute platformOnly>
                        <SupplierOnboarding />
                      </ProtectedRoute>
                    }
                  />
                  {/*
                    R-18: Call Reminders is corporate-scoped, so it needs `:id`.

                    `Reminders.tsx:15` returns `<Navigate to="/corporates" replace />`
                    when `useParams().id` is absent, and its only route was the
                    id-less `/reminders` — so the page could never render, on any
                    path. MEASURED in Chrome: `/reminders` redirected to
                    `/corporates`, and `/corporate/440/reminders` was a 404. Both
                    doors bricked, while `process-call-reminders` runs on cron and
                    writes rows nobody could see.

                    `/reminders` is kept as a redirect rather than deleted: it is a
                    real path that has shipped, and silently 404ing a URL someone may
                    have bookmarked is a worse answer than sending them somewhere.
                    Guarded by src/test/id-guarded-pages-are-routed-with-id.test.ts.
                  */}
                  <Route path="/reminders" element={<Navigate to="/corporates" replace />} />
                  <Route
                    path="/corporate/:id/reminders"
                    element={
                      <ProtectedRoute corporateRoute>
                        <Reminders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/conversation-guide"
                    element={
                      <ProtectedRoute platformOnly>
                        <TreasuryConversationGuidePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/conversation-guide/new"
                    element={
                      <ProtectedRoute platformOnly>
                        <TreasuryConversationGuideCreatePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/conversation-guide/:guideId"
                    element={
                      <ProtectedRoute platformOnly>
                        <TreasuryConversationGuideDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account/security"
                    element={<Navigate to="/admin/settings/security" replace />}
                  />
                  {/*
                   * `/approvals` is a bookmark alias, kept because `page-titles.ts` and
                   * `navigation-config.ts` already treat it as one of Enrollments' paths.
                   * It used to carry `?tab=approvals`, which stopped resolving to anything when
                   * the approvals queue was deleted on 2026-08-23 — a query param pointing at a
                   * tab that no longer exists is how a URL starts lying about what it shows.
                   */}
                  <Route path="/approvals" element={<Navigate to="/enrollments" replace />} />
                  <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
                  <Route
                    path="/admin/mastra"
                    element={
                      <ProtectedRoute platformOnly requiredRoles={["admin"]}>
                        <Navigate to="/admin/settings" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AdminSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings/security"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AccountSecurity />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AdminUsers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users/:userId"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AdminUserDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/data-hygiene"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <DataHygienePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/audit-log"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AdminAuditLog />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/negotiation-templates/create"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AdminNegotiationTemplateCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/design-system"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <DesignSystem />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/can-spam-readiness"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <CanSpamReadiness />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/negotiation-templates/:templateId/edit"
                    element={
                      <ProtectedRoute requiredRoles={["admin"]}>
                        <AdminNegotiationTemplateEdit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/crossover-suppliers"
                    element={<Navigate to="/intelligence" replace />}
                  />
                  <Route
                    path="/corporate/:id/strategy/session/:sessionId"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CampaignStrategyPlanning />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaign-strategy/:sessionId"
                    element={
                      <ProtectedRoute platformOnly>
                        <CampaignStrategyPlanning />
                      </ProtectedRoute>
                    }
                  />

                  {/* Corporate detail routes */}
                  <Route path="/corporate/:id" element={<Navigate to="goals" replace />} />
                  <Route
                    path="/corporate/:id/playbook"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporatePlaybook />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/proposal"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateProposal />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="planning" replace />} />
                    <Route path="planning" element={<ProposalPlanning />} />
                    <Route path="proposal" element={<ProposalOverview />} />
                    <Route path="opportunity-map" element={<Navigate to="../planning" relative="path" replace />} />
                    <Route path="strategy-planner" element={<Navigate to="../proposal" relative="path" replace />} />
                    <Route path="scenario-lab" element={<Navigate to="../planning" relative="path" replace />} />
                    <Route path="cohorts" element={<Navigate to="../planning" relative="path" replace />} />
                    <Route path="decision-brief" element={<Navigate to="../planning" relative="path" replace />} />
                    <Route path="proof" element={<Navigate to="../planning" relative="path" replace />} />
                    <Route path="decision-council" element={<Navigate to="../planning" relative="path" replace />} />
                    <Route path="activation-plan" element={<Navigate to="../planning" relative="path" replace />} />
                  </Route>
                  {/* Retired preview-only workspaces. Keep old links recoverable while
                  making Proposal Suppliers the single canonical source view. */}
                  <Route
                    path="/corporate/:id/cohorts"
                    element={<Navigate to="../proposal/planning" relative="path" replace />}
                  />
                  <Route
                    path="/corporate/:id/activity"
                    element={<Navigate to="../proposal/planning" relative="path" replace />}
                  />
                  <Route
                    path="/corporate/:id/documents"
                    element={<Navigate to="../proposal/planning" relative="path" replace />}
                  />
                  <Route
                    path="/corporate/:id/overview"
                    element={<Navigate to="../goals" relative="path" replace />}
                  />
                  <Route
                    path="/corporate/:id/contacts"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateContacts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/contacts/create"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateContactCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/contacts/:contactId"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateContactDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/vendors"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateTradeProfiles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/vendors/create"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateVendorCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/leads/new"
                    element={
                      <ProtectedRoute corporateRoute>
                        <LeadEntryForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/vendors/:profileId"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateTradeProfileDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/campaigns"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateCampaigns />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/campaigns/create"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateCampaignCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/email-studio"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateEmailStudio />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/templates"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateTemplates />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/compliance-center"
                    element={
                      <ProtectedRoute corporateRoute>
                        <ComplianceCenter />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/policies"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporatePolicies />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/details"
                    element={
                      <ProtectedRoute corporateRoute>
                        <Navigate to="../settings?tab=details" relative="path" replace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/strategy"
                    element={<Navigate to="../policies" relative="path" replace />}
                  />
                  <Route
                    path="/corporate/:id/policies/configurations/create"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateStrategyConfigCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/policies/templates/create"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateNegotiationTemplateCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/goals"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateGoals />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/goals/create"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateGoalCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/goals/:goalId"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateGoalCreate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/financial-settings"
                    element={<Navigate to="../settings?tab=financial" relative="path" replace />}
                  />
                  {/*
                    Full-page wizard re-housing of the GuruFocus import — the
                    dialog-based flow (GurufocusUploadDialog, reached today from
                    CorporatesCreate.tsx and FinancialProfileForm.tsx) did not
                    follow this app's wizard pattern. Neither existing entry
                    point is rewired to this route yet; it is a standalone,
                    directly-reachable page for now.
                  */}
                  <Route
                    path="/corporate/:id/uploadfinancialreports"
                    element={
                      <ProtectedRoute corporateRoute>
                        <UploadFinancialReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/supplier-onboarding"
                    element={
                      <ProtectedRoute corporateRoute>
                        <SupplierCorporateOnboardingHub />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/supplier-onboarding/:packageId"
                    element={
                      <ProtectedRoute corporateRoute>
                        <SupplierCorporateOnboardingPackageWizard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/settings"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateSettings />
                      </ProtectedRoute>
                    }
                  />

                  {/* R19: corporate-scoped, portal-safe siblings of the platformOnly
                  /ap-spend-files/upload and /ap-spend-files/:fileId routes below.
                  Portal buyer_standard users must reach these — from the
                  onboarding hub, the "Upload AP Spend File" page action on
                  /corporate/:id/campaigns, and the AP spend files table row/
                  filename click — without ever hitting the platformOnly guard. */}
                  <Route
                    path="/corporate/:id/ap-spend-files/upload"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateAPSpendFileUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/ap-spend-files/:fileId"
                    element={
                      <ProtectedRoute corporateRoute>
                        <APSpendFileDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/corporate/:id/ap-spend-files"
                    element={
                      <ProtectedRoute corporateRoute>
                        <CorporateAPSpendFiles />
                      </ProtectedRoute>
                    }
                  />

                  {/* Backward compatibility redirects */}
                  <Route path="/ap-spend-files" element={<Navigate to="/corporates" replace />} />
                  <Route path="/spend-files" element={<Navigate to="/corporates" replace />} />
                  <Route
                    path="/corporate/:id/files"
                    element={<Navigate to="../ap-spend-files" relative="path" replace />}
                  />
                  <Route
                    path="/corporate/:id/trade-profiles"
                    element={<Navigate to="../vendors" relative="path" replace />}
                  />
                  <Route path="/action-items" element={<Navigate to="/action-center" replace />} />
                </Route>

                {/* Issuer Hub — standalone layout with issuer-specific sidebar */}
                <Route
                  element={
                    <ProtectedRoute>
                      <IssuerHubLayoutRoute />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="/issuer-hub"
                    element={
                      <IssuerHubGuard>
                        <Navigate to="/issuer-hub/pipeline" replace />
                      </IssuerHubGuard>
                    }
                  />
                  <Route
                    path="/issuer-hub/pipeline"
                    element={
                      <IssuerHubGuard>
                        <IssuerPipelinePage />
                      </IssuerHubGuard>
                    }
                  />
                  <Route
                    path="/issuer-hub/campaigns"
                    element={
                      <IssuerHubGuard>
                        <IssuerCampaignOverviewPage />
                      </IssuerHubGuard>
                    }
                  />
                  <Route
                    path="/issuer-hub/modeling"
                    element={
                      <IssuerHubGuard>
                        <IssuerFinancialModelingPage />
                      </IssuerHubGuard>
                    }
                  />
                  <Route
                    path="/issuer-hub/quota"
                    element={
                      <IssuerHubGuard>
                        <IssuerQuotaPage />
                      </IssuerHubGuard>
                    }
                  />
                  <Route
                    path="/issuer-hub/prospecting"
                    element={
                      <IssuerHubGuard>
                        <IssuerProspectingPagesPage />
                      </IssuerHubGuard>
                    }
                  />
                </Route>

                {/* Buyer Hub — standalone layout for Hub-sent A1 buyers */}
                <Route
                  path="/buyer-hub/:corporateId"
                  element={
                    <ProtectedRoute>
                      <BuyerHubGuard>
                        <BuyerHubLayoutRoute />
                      </BuyerHubGuard>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<BuyerHubHome />} />
                  <Route path="spend" element={<BuyerHubSpendPage />} />
                  <Route path="proposal" element={<BuyerHubProposalPage />} />
                  <Route path="planning" element={<BuyerHubPlanningPage />} />
                  <Route path="reporting" element={<BuyerHubReportingPage />} />
                </Route>

                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </SentryRoutes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
        <Analytics />
      </CorporateAccessProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
